import { useInvalidate, useList, useOne } from '@refinedev/core';
import { notification } from 'antd';
import { NotificationInstance } from 'antd/es/notification/interface';
import { PLATFORM_DATA_CONFIG } from 'common/constants';
import { safeParseStringArray } from 'common/utils/string.util';
import dayjs from 'dayjs';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuth } from 'src/auth';

import EntitlementEnum from './entitlements.enum';
import { FarmProvider as FarmsProvider } from './farm/farm.provider';
import OnboardingStatusEnum from './onboarding-status.enum';
import { Licensing, LicensingPlan, Organization } from './organization.model';

interface OrganizationContextProps {
  organizations: Organization[];
  plan: LicensingPlan | null;
  licensing: Licensing | null;
  entitlements: string[];
  currentOrganization: Organization | null;
  currentOrganizationId: string | null;
  currentOrganizationOnboardingStatus?: OnboardingStatusEnum | null;
  isOnboardingEnabled: boolean;
  currentWorkspaceId: string | null;
  // Entities picked in My Cropwise. They can precede the product context: an
  // organization whose onboarding is still pending never becomes
  // `currentOrganizationId`, but the topbar still needs both ids so the Settings
  // MFE — and the guided onboarding it hosts — can render before a farm is
  // chosen. See FMS-2589.
  selectedWorkspaceId: string | null;
  selectedOrganizationId: string | null;
  setSelectedWorkspaceId: (workspaceId: string | null) => void;
  setSelectedOrganizationId: (organizationId: string | null) => void;
  setCurrentOrganizationId: (organizationId: string | null) => void;
  isLoading: boolean;
  isFetched: boolean;
  error: unknown;
  apiNotification?: NotificationInstance;
}

const OrganizationContext = createContext<OrganizationContextProps>({
  organizations: [],
  plan: null,
  licensing: null,
  entitlements: [],
  currentOrganization: null,
  currentOrganizationId: null,
  currentOrganizationOnboardingStatus: null,
  isOnboardingEnabled: false,
  currentWorkspaceId: null,
  selectedWorkspaceId: null,
  selectedOrganizationId: null,
  setSelectedWorkspaceId: () => null,
  setSelectedOrganizationId: () => null,
  setCurrentOrganizationId: () => null,
  isLoading: false,
  isFetched: false,
  error: null,
});

const OrganizationProvider = ({ children }: PropsWithChildren) => {
  const [apiNotification, contextHolder] = notification.useNotification();
  const { user } = useAuth();
  const [currentOrganizationId, setCurrentOrganizationId] = useState<
    string | null
  >(null);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(
    null,
  );
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<
    string | null
  >(null);
  const invalidate = useInvalidate();

  const allEntitlements = useMemo(() => Object.values(EntitlementEnum), []);
  const { entitlementsAllEnableOrgId, enableOnboardingFms2080 } = useFlags();

  const openOrgsId = useMemo(
    () => safeParseStringArray(entitlementsAllEnableOrgId),
    [entitlementsAllEnableOrgId],
  );
  const isOpenOrgsId = openOrgsId.includes(currentOrganizationId ?? '');

  const { data, error, isLoading, isFetching, isFetched } =
    useList<Organization>({
      meta: {
        isOptionalOrganization: true,
      },
      resource: 'organization-user',
      queryOptions: {
        enabled: !!user,
      },
    });

  const { data: licensingData } = useOne<Licensing>({
    dataProviderName: 'platformV2',
    resource: 'licensing',
    id: 'status',
    meta: {
      params: {
        org: currentOrganizationId,
        app: 'all',
      },
    },
    queryOptions: {
      enabled: !!currentOrganizationId,
    },
  });

  const plan = useMemo(
    () =>
      licensingData?.data?.apps
        .filter((app) => app.id === PLATFORM_DATA_CONFIG.APP_ID)
        .find(
          (app) =>
            dayjs(app.contract.start).isBefore(dayjs()) &&
            dayjs(app.contract.end).isAfter(dayjs()),
        )?.plan ?? null,
    [licensingData],
  );

  const entitlements = useMemo(() => {
    if (isOpenOrgsId) {
      return allEntitlements;
    }
    return plan?.entitlements.map((entitlement) => entitlement.key) ?? [];
  }, [plan, isOpenOrgsId, allEntitlements]);

  const organizations = useMemo(() => {
    const allOrgs = data?.data ?? [];
    // When onboarding is disabled, filter out orgs without balanceConfigured
    if (!enableOnboardingFms2080) {
      return allOrgs.filter((org) => org.isBalanceConfigured !== false);
    }
    return allOrgs;
  }, [data, enableOnboardingFms2080]);

  const currentOrganization = useMemo(() => {
    const organization = organizations.find(
      (org) => org.id === currentOrganizationId,
    );
    return organization || null;
  }, [organizations, currentOrganizationId]);

  useEffect(() => {
    if (currentOrganizationId) {
      invalidate({
        invalidates: ['all'],
      });
    }
  }, [currentOrganizationId]);

  const currentOrganizationOnboardingStatus = useMemo(() => {
    if (!enableOnboardingFms2080) {
      return OnboardingStatusEnum.COMPLETED;
    }
    return currentOrganization?.onboardingStatus;
  }, [currentOrganization?.onboardingStatus, enableOnboardingFms2080]);

  const currentWorkspaceId = useMemo(() => {
    const workspaceId = currentOrganization?.crn.match(
      /workspace:([a-f0-9-]+)/,
    );
    return workspaceId ? workspaceId[1] : null;
  }, [currentOrganization]);

  const value = useMemo(
    () => ({
      organizations,
      plan,
      licensing: licensingData?.data ?? null,
      entitlements,
      currentOrganization,
      currentOrganizationId,
      currentOrganizationOnboardingStatus,
      isOnboardingEnabled: enableOnboardingFms2080,
      currentWorkspaceId,
      selectedWorkspaceId,
      selectedOrganizationId,
      setSelectedWorkspaceId,
      setSelectedOrganizationId,
      setCurrentOrganizationId,
      isLoading: isLoading ?? isFetching,
      isFetched,
      error,
      apiNotification,
    }),
    [
      organizations,
      entitlements,
      plan,
      licensingData,
      currentOrganization,
      currentOrganizationId,
      currentOrganizationOnboardingStatus,
      enableOnboardingFms2080,
      currentWorkspaceId,
      selectedWorkspaceId,
      selectedOrganizationId,
      isLoading,
      isFetching,
      error,
      apiNotification,
    ],
  );

  return (
    <OrganizationContext.Provider value={value}>
      {contextHolder}
      <FarmsProvider>{children}</FarmsProvider>
    </OrganizationContext.Provider>
  );
};

const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error(
      'useOrganization must be used within an OrganizationProvider',
    );
  }
  return context;
};

export { OrganizationProvider, useOrganization };
